import {Box, Button, FormControl, IconButton, Stack, Typography, useTheme} from '@mui/material';
import React, {Suspense} from 'react';
import {FacialBiometricsHeader} from '../FacialBiometricsHeader';
import {ResponsiveCard} from '../ResponsiveCard';

import {useMediaQueries} from 'presentation/pages/private';
import {CTAButtonFixed, IconIllustration} from 'presentation/components';
import {useFileHandler} from './hooks/useFileHandler';

import {StyledLoading, FileInput, Container, Label, PreviewWrapper} from './styles';
import {ALLOWED_EXTENSIONS} from './constants';
import Icon from 'presentation/icons';

import {translations} from 'presentation/translations-new';
import {Loading} from 'presentation/components/Loading/Loading';
import {ErrorCard} from '../ErrorCard';
import {DocumentUploadProps} from './types';

const {
	documentPhoto: {
		documentUpload: {advice, formLabel, primaryButton, concluded, title},
	},
	loadingMessage,
} = translations['pt-br'].others.facialBiometric;

export function DocumentUpload({onResend}: DocumentUploadProps) {
	const {isTablet} = useMediaQueries();
	const theme = useTheme();

	const {
		documentError,
		onDrop,
		onDragOver,
		onDragLeave,
		isOnDrop,
		isLoading,
		onChange,
		file,
		onSubmit,
		setFile,
		errorCode,
	} = useFileHandler();

	const LoadingContent = () => (
		<Loading text={loadingMessage} minHeight={isTablet ? '100%' : '527px'} />
	);

	return (
		<>
			<FacialBiometricsHeader
				variant={errorCode ? 'default' : 'white'}
				showAbbandonFlowAlert={!!errorCode}
			/>
			<Stack alignItems="center" height="100%">
				{errorCode ? (
					<Suspense
						fallback={
							<ResponsiveCard>
								<LoadingContent />
							</ResponsiveCard>
						}>
						<ErrorCard errorCode={errorCode} onRetry={onSubmit} onResend={onResend} />
					</Suspense>
				) : (
					<>
						<ResponsiveCard>
							{isLoading ? (
								<LoadingContent />
							) : (
								<>
									<Typography variant="body1" fontWeight={600} mb={3}>
										{title}
									</Typography>

									{file ? (
										<PreviewWrapper>
											<Icon
												size={32}
												name="check-circle-filled"
												color={theme.colors.light.success.hue}
											/>
											<Stack ml={1.5}>
												<Typography variant="subtitle1">
													{file?.name}
												</Typography>
												<Typography variant="body2">
													{Math.round((file?.size ?? 0) / 1024)}kb •{' '}
													{concluded}
												</Typography>
											</Stack>
											<IconButton
												size="small"
												sx={{ml: 'auto'}}
												onClick={() => setFile(null)}>
												<Icon size={14} name="close-filled" />
											</IconButton>
										</PreviewWrapper>
									) : (
										<Container
											$isError={documentError.isError}
											justifyContent="center"
											alignItems="center"
											onDrop={onDrop}
											onDragOver={onDragOver}
											onDragLeave={onDragLeave}
											$isOnDrop={isOnDrop}
											spacing={1}>
											{isLoading ? (
												<StyledLoading />
											) : (
												<IconIllustration
													customIconName="upload-file-filled"
													variant={
														documentError.isError ? 'error' : 'primary'
													}
													size="custom"
												/>
											)}

											<FormControl>
												<Label htmlFor="file" as="label">
													{formLabel}
												</Label>

												<FileInput
													id="file"
													type="file"
													accept={ALLOWED_EXTENSIONS.join(',')}
													onChange={onChange}
												/>
											</FormControl>

											<Box sx={{textAlign: 'center'}}>
												{documentError.message ? (
													<Typography variant="body2" color="error">
														{documentError.message}
													</Typography>
												) : null}

												<Typography
													variant="body2"
													textAlign="center"
													color={
														documentError.isError
															? 'error'
															: 'text.secondary'
													}>
													{advice}
												</Typography>
											</Box>
										</Container>
									)}
									{!isTablet && (
										<Stack
											direction="row"
											justifyContent="center"
											width="100%"
											mt={3}>
											<Button onClick={onSubmit}>{primaryButton}</Button>
										</Stack>
									)}
								</>
							)}
						</ResponsiveCard>

						{isTablet && !isLoading ? (
							<CTAButtonFixed.Root $isTablet={isTablet} sx={{bottom: 0}}>
								<CTAButtonFixed.Action onClick={onSubmit} fullWidth>
									{primaryButton}
								</CTAButtonFixed.Action>
							</CTAButtonFixed.Root>
						) : null}
					</>
				)}
			</Stack>
		</>
	);
}
